import {
  makeDataState,
  makePaginationState,
  STATUSES,
  filterUnique,
  concatUnique,
} from 'app/utils/redux';

import * as t from './types';

const initialState = {
  objectType: null,
  objectId: null,
  story: makeDataState(),
  communityPost: makeDataState(),
  episode: makeDataState(),
  bestComments: makePaginationState(),
  fixedComments: makePaginationState(),
  comments: makePaginationState(),
  comment: makeDataState(),
  replyComments: makePaginationState(),
};

const _protectCachedLikeCount = (items) =>
  items.map((item) => {
    if (item.isLiked && item.likeCount === 0) {
      return {
        ...item,
        likeCount: 1,
      };
    }

    return item;
  });

const _likeCommentIterator = (item, commentId, isAuthor) => {
  if (item.id === commentId) {
    return {
      ...item,
      isLiked: !item.isLiked,
      likeCount: item.isLiked ? item.likeCount - 1 : item.likeCount + 1,
      ...(isAuthor ? { isLikedByOwner: !item.isLiked } : {}),
    };
  }

  return item;
};

const checkAuthor = (state, userId, commentListType) => {
  if (commentListType === 'story') {
    return userId === state.story.data.user.id;
  } else if (commentListType === 'post') {
    return userId === state.communityPost.data.user.id;
  }
  return false;
};

export default (state = initialState, action) => {
  switch (action.type) {
    // set story
    case t.SET_STORY:
      return {
        ...state,
        story: action.story,
      };

    // set community post
    case t.SET_COMMUNITY_POST:
      return {
        ...state,
        communityPost: action.communityPost,
      };

    // set episode
    case t.SET_EPISODE:
      return {
        ...state,
        episode: action.episode,
      };

    // fetch comments
    case `${t.FETCH_COMMENTS}_PENDING`:
      return {
        ...state,
        comments: {
          ...state.comments,
          status: STATUSES.PENDING,
        },
      };
    case `${t.FETCH_COMMENTS}_SUCCESS`:
      return {
        ...state,
        objectType: action.meta.objectType,
        objectId: action.meta.objectId,
        comments: {
          status: STATUSES.SUCCESS,
          isLoaded: true,
          pagination: {
            ...action.payload.result,
            items: concatUnique(
              state.comments.pagination.items,
              filterUnique(
                _protectCachedLikeCount(action.payload.result.items),
                state.fixedComments.pagination.items,
                state.bestComments.pagination.items
              )
            ),
          },
        },
      };

    case `${t.FETCH_COMMENTS}_ERROR`:
      return {
        ...state,
        comments: {
          ...state.comments,
          status: STATUSES.ERROR,
        },
      };

    // fetch comment
    case `${t.FETCH_COMMENT}_PENDING`:
      return {
        ...state,
        comment: {
          ...state.comment,
          status: STATUSES.PENDING,
        },
      };

    case `${t.FETCH_COMMENT}_SUCCESS`:
      return {
        ...state,
        comment: {
          ...state.comment,
          status: STATUSES.SUCCESS,
          isLoaded: true,
          data: action.payload.result,
        },
      };

    case `${t.FETCH_COMMENT}_ERROR`:
      return {
        ...state,
        comment: {
          ...state.comment,
          status: STATUSES.ERROR,
        },
      };

    // fetch and add
    case `${t.FETCH_COMMENT_ADD}_SUCCESS`:
      return {
        ...state,
        comments: {
          ...state.comments,
          pagination: {
            ...state.comments.pagination,
            items: [action.payload.result].concat(
              state.comments.pagination.items
            ),
          },
        },
      };

    // fetch reply comments
    case `${t.FETCH_REPLY_COMMENTS}_PENDING`:
      return {
        ...state,
        replyComments: {
          ...state.replyComments,
          status: STATUSES.PENDING,
        },
      };

    case `${t.FETCH_REPLY_COMMENTS}_SUCCESS`:
      return {
        ...state,
        replyComments: {
          status: STATUSES.SUCCESS,
          isLoaded: true,
          pagination: {
            ...action.payload.result,
            items: concatUnique(
              state.replyComments.pagination.items,
              _protectCachedLikeCount(action.payload.result.items)
            ),
          },
        },
      };

    case `${t.FETCH_REPLY_COMMENTS}_ERROR`:
      return {
        ...state,
        replyComments: {
          ...state.replyComments,
          status: STATUSES.ERROR,
        },
      };

    // post comment
    case `${t.POST_COMMENT}_SUCCESS`:
      return {
        ...state,
        comments: {
          status: STATUSES.SUCCESS,
          isLoaded: true,
          pagination: {
            ...state.comments.pagination,
            totalCount: state.comments.pagination.totalCount + 1,
            items: [action.payload.result].concat(
              _protectCachedLikeCount(state.comments.pagination.items)
            ),
          },
        },
      };
    case `${t.POST_COMMENT}_ERROR`:
      return {
        ...state,
        comments: {
          ...state.comments,
          status: STATUSES.ERROR,
        },
      };

    case `${t.POST_REPLY_COMMENT}_SUCCESS`:
      return {
        ...state,
        replyComments: {
          status: STATUSES.SUCCESS,
          isLoaded: true,
          pagination: {
            ...state.replyComments.pagination,
            totalCount: state.replyComments.pagination.totalCount + 1,
            items: [action.payload.result].concat(
              _protectCachedLikeCount(state.replyComments.pagination.items)
            ),
          },
        },
      };
    case `${t.POST_REPLY_COMMENT}_ERROR`:
      return {
        ...state,
        replyComments: {
          ...state.replyComments,
          status: STATUSES.ERROR,
        },
      };

    // delete or delete comment
    case `${t.DELETE_COMMENT}_SUCCESS`:
    case `${t.HIDE_COMMENT}_SUCCESS`: {
      const { commentId, isReply } = action.meta;

      const _removeComment = (commentType) => ({
        [commentType]: {
          ...state[commentType],
          pagination: {
            ...state[commentType].pagination,
            items: state[commentType].pagination.items.filter(
              (item) => item.id !== commentId
            ),
          },
        },
      });

      if (isReply) {
        return {
          ...state,
          ..._removeComment('replyComments'),
        };
      }

      return {
        ...state,
        ..._removeComment('bestComments'),
        ..._removeComment('fixedComments'),
        ..._removeComment('comments'),
      };
    }

    // like comment
    case `${t.LIKE_COMMENT}_SUCCESS`: {
      const { userId, commentId, isReply, commentListType } = action.meta;
      const isAuthor = checkAuthor(state, userId, commentListType);

      const _likeComment = (commentType) => ({
        [commentType]: {
          ...state[commentType],
          pagination: {
            ...state[commentType].pagination,
            items: state[commentType].pagination.items.map((item) =>
              _likeCommentIterator(item, commentId, isAuthor)
            ),
          },
        },
      });

      if (isReply) {
        return {
          ...state,
          ..._likeComment('replyComments'),
        };
      }

      return {
        ...state,
        ..._likeComment('bestComments'),
        ..._likeComment('fixedComments'),
        ..._likeComment('comments'),
      };
    }

    case `${t.LIKE_REPLY_TARGET_COMMENT}_SUCCESS`: {
      const { userId, commentId, commentListType } = action.meta;
      const isAuthor = checkAuthor(state, userId, commentListType);

      return {
        ...state,
        bestComments: {
          ...state.bestComments,
          pagination: {
            ...state.bestComments.pagination,
            items: state.bestComments.pagination.items.map((item) =>
              _likeCommentIterator(item, commentId, isAuthor)
            ),
          },
        },
        comments: {
          ...state.comments,
          pagination: {
            ...state.comments.pagination,
            items: state.comments.pagination.items.map((item) =>
              _likeCommentIterator(item, commentId, isAuthor)
            ),
          },
        },
        comment: {
          ...state.comment,
          data: _likeCommentIterator(state.comment.data, commentId, isAuthor),
        },
      };
    }

    // fetch best comments
    case `${t.FETCH_BEST_COMMENTS}_PENDING`:
      return {
        ...state,
        bestComments: {
          ...state.bestComments,
          status: STATUSES.PENDING,
        },
      };
    case `${t.FETCH_BEST_COMMENTS}_SUCCESS`:
      return {
        ...state,
        bestComments: {
          status: STATUSES.SUCCESS,
          isLoaded: true,
          pagination: {
            ...state.bestComments.pagination,
            items: filterUnique(
              _protectCachedLikeCount(action.payload.result.items),
              state.bestComments.pagination.items
            ),
          },
        },
      };
    case `${t.FETCH_BEST_COMMENTS}_ERROR`:
      return {
        ...state,
        bestComments: {
          ...state.bestComments,
          status: STATUSES.ERROR,
        },
      };

    // fetch fixed comments
    case `${t.FETCH_FIXED_COMMENTS}_PENDING`:
      return {
        ...state,
        fixedComments: {
          ...state.fixedComments,
          status: STATUSES.PENDING,
        },
      };
    case `${t.FETCH_FIXED_COMMENTS}_SUCCESS`:
      return {
        ...state,
        fixedComments: {
          status: STATUSES.SUCCESS,
          isLoaded: true,
          pagination: {
            ...state.fixedComments.pagination,
            items: _protectCachedLikeCount(action.payload.result.items),
          },
        },
      };
    case `${t.FETCH_FIXED_COMMENTS}_ERROR`:
      return {
        ...state,
        fixedComments: {
          ...state.fixedComments,
          status: STATUSES.ERROR,
        },
      };

    case t.SET_COMMENT_OBJECT:
      return {
        ...state,
        prevCommentObject: action.meta,
      };

    // reset
    case t.RESET:
      return {
        ...state,
        objectType: null,
        objectId: null,
        bestComments: makePaginationState(),
        comments: makePaginationState(),
      };

    case t.RESET_REPLY_COMMENTS:
      return {
        ...state,
        replyComments: makePaginationState(),
        comment: makeDataState(),
      };

    default:
      return { ...state };
  }
};



// WEBPACK FOOTER //
// ./src/app/redux/front/comment/reducer.js